import { ChoiceChips, FilterMenu, formatNumber, type ChoiceTone } from '@platform/design-system';
import { usePageContext } from '../usePageContext.ts';
import { chipViewsOf, MORE_VIEWS, type OrderView } from './orders.ts';

const VIEW_TONES: Readonly<Record<OrderView, ChoiceTone>> = {
  all: 'neutral',
  to_confirm: 'accent',
  confirmed: 'info',
  to_print: 'warning',
  printed: 'neutral',
  in_delivery: 'brand',
  missing: 'danger',
  delivered: 'success',
  returns: 'neutral',
  to_receive: 'warning',
  exchanges: 'neutral',
  canceled: 'neutral',
};

export type OrderViewChipsProps = {
  view: OrderView;
  counts: ReadonlyMap<OrderView, number> | undefined;
  onChange: (view: OrderView) => void;
};

export const OrderViewChips = ({ view, counts, onChange }: OrderViewChipsProps) => {
  const { t, locale } = usePageContext();
  const labelOf = (value: OrderView) => t(`orders.view_${value}`);
  const countOf = (value: OrderView) => {
    const count = counts?.get(value);
    return count === undefined ? '' : ` (${formatNumber(count, locale)})`;
  };
  return (
    <ChoiceChips
      legend={t('orders.view')}
      hideLegend
      locale={locale}
      value={view}
      choices={chipViewsOf(counts, view).map((value) => ({
        value,
        label: labelOf(value),
        count: counts?.get(value),
        tone: VIEW_TONES[value],
      }))}
      onChange={onChange}
      trailing={
        <FilterMenu
          label={t('orders.moreViews')}
          options={MORE_VIEWS.map((value) => ({ value, label: `${labelOf(value)}${countOf(value)}` }))}
          value={MORE_VIEWS.find((value) => value === view) ?? null}
          clearLabel={t('orders.backToAllOrders')}
          onChange={(chosen) => {
            onChange(MORE_VIEWS.find((value) => value === chosen) ?? 'all');
          }}
        />
      }
    />
  );
};
